import { useState, useEffect } from 'react';
import { Plus, Trash2, Save, Calendar, DollarSign, Loader2, EyeOff, Pencil } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Badge } from '~/components/ui/badge';
import { Checkbox } from '~/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '~/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { useUpdateTuitionOptions } from './useUpdateTuitionOptions';
import { useUpdateTuitionPaymentOptions } from './useUpdateTuitionPaymentOptions';
import { useUpdateEnrollmentFee } from './useUpdateEnrollmentFee';
import type { TuitionOptionsSetting, TuitionOptionItem, TuitionPaymentOption, EnrollmentFeeSetting } from './types';

/**
 * Runs a save and resolves with the messages to show under the editor: the
 * server's refusals, or one line when the request itself failed. Empty means
 * it saved.
 */
async function saveErrorsFrom(
  run: () => Promise<readonly { message: string }[] | null | undefined>
): Promise<string[]> {
  try {
    return ((await run()) ?? []).map((error) => error.message);
  } catch {
    return ["Couldn't save. Try again."];
  }
}

function SaveErrors({ errors }: { errors: string[] }) {
  if (errors.length === 0) return null;
  return (
    <div role="alert" className="space-y-1 text-sm text-destructive">
      {errors.map((message) => (
        <p key={message}>{message}</p>
      ))}
    </div>
  );
}

/**
 * Format cents to dollar display
 */
function formatCentsToDollars(cents: number) {
  return (cents / 100).toFixed(2);
}

/**
 * Parse dollar input to cents
 */
function parseDollarsToCents(dollars: string) {
  const parsed = parseFloat(dollars);
  if (isNaN(parsed)) return 0;
  return Math.round(parsed * 100);
}

interface TuitionOptionsEditorProps {
  /** The school year these tuition options belong to. */
  schoolYearId: string;
  setting?: TuitionOptionsSetting;
  onSaved?: () => void;
}

/** A tuition option item augmented with UI-only fields for editing */
interface TuitionOptionEditItem extends TuitionOptionItem {
  _key: string;
  _costDisplay: string;
}

/**
 * TuitionOptionsEditor - Reusable component for editing tuition options
 */
export function TuitionOptionsEditor({ schoolYearId, setting, onSaved }: TuitionOptionsEditorProps) {
  const [items, setItems] = useState<TuitionOptionEditItem[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  const [updateTuitionOptions, { loading }] = useUpdateTuitionOptions();
  const [saveErrors, setSaveErrors] = useState<string[]>([]);

  // Initialize from setting
  useEffect(() => {
    if (setting?.items) {
      setItems(setting.items.map((item, index) => ({
        ...item,
        _key: `${index}-${Date.now()}`,
        _costDisplay: formatCentsToDollars(item.costCents),
      })));
      setHasChanges(false);
    }
  }, [setting]);

  const handleAdd = () => {
    const newKey = `option-${Date.now()}`;
    setItems([...items, {
      key: newKey,
      name: '',
      costCents: 0,
      description: '',
      _key: `new-${Date.now()}`,
      _costDisplay: '0.00',
    }]);
    setHasChanges(true);
  };

  const handleRemove = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
    setHasChanges(true);
  };

  const handleChange = (index: number, field: string, value: string) => {
    const newItems = [...items];
    if (field === 'costCents') {
      newItems[index] = {
        ...newItems[index],
        _costDisplay: value,
        costCents: parseDollarsToCents(value),
      };
    } else {
      newItems[index] = { ...newItems[index], [field]: value };
    }
    setItems(newItems);
    setHasChanges(true);
  };

  const handleSave = async () => {
    const usedKeys = new Set<string>();
    const tuitionOptions = items
      .filter(item => item.name.trim())
      .map(({ key, name, costCents, description }) => {
        let finalKey = key || name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
        // Ensure key uniqueness
        if (usedKeys.has(finalKey)) {
          finalKey = `${finalKey}-${Date.now()}`;
        }
        usedKeys.add(finalKey);
        return { key: finalKey, name, costCents, description: description || null };
      });

    const errors = await saveErrorsFrom(async () => {
      const { data } = await updateTuitionOptions({
        variables: { input: { schoolYearId, tuitionOptions } },
      });
      return data?.updateTuitionOptions?.userErrors;
    });
    setSaveErrors(errors);
    if (errors.length === 0) {
      setHasChanges(false);
      onSaved?.();
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Tuition Options</CardTitle>
        <CardDescription>
          {setting?.description || 'Tuition options define the available enrollment pricing plans families can choose from.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {items.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            No tuition options configured. Add your first option below.
          </p>
        ) : (
          <div className="space-y-3">
            {items.map((item, index) => (
              <div key={item._key} className="flex items-start gap-3 p-3 border rounded-lg">
                <div className="flex-1 space-y-2">
                  <div className="flex gap-2">
                    <Input
                      value={item.name}
                      onChange={(e) => handleChange(index, 'name', e.target.value)}
                      placeholder="Option name (e.g., 4-Day Tuition)"
                      className="flex-1"
                    />
                    <div className="relative w-32">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input
                        type="text"
                        value={item._costDisplay}
                        onChange={(e) => handleChange(index, 'costCents', e.target.value)}
                        placeholder="0.00"
                        className="pl-7"
                      />
                    </div>
                  </div>
                  <Input
                    value={item.description || ''}
                    onChange={(e) => handleChange(index, 'description', e.target.value)}
                    placeholder="Description (optional)"
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemove(index)}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        )}

        <SaveErrors errors={saveErrors} />

        <div className="flex justify-between pt-4 border-t">
          <Button type="button" variant="outline" onClick={handleAdd}>
            <Plus className="h-4 w-4 mr-2" />
            Add Tuition Option
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={!hasChanges || loading}
          >
            <Save className="h-4 w-4 mr-2" />
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Helper to format cents to dollars
const formatCents = (cents: number) => {
  return (cents / 100).toFixed(2);
};

// Helper to parse dollars to cents
const parseToCents = (dollars: string) => {
  const num = parseFloat(dollars);
  return isNaN(num) ? 0 : Math.round(num * 100);
};

// Schedule type labels for display
const SCHEDULE_TYPE_LABELS: Record<string, string> = {
  annual: 'Annual',
  biannual: 'Semi-Annual',
  monthly: 'Monthly',
};

// Common billing day options
const BILLING_DAY_OPTIONS = [
  { value: 1, label: '1st of month' },
  { value: 15, label: '15th of month' },
];

// Month options for start month selection
const MONTH_OPTIONS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

/** Local payment option used for edit state; matches TuitionPaymentOption but description may be absent on new entries */
interface PaymentOptionEditState {
  key: string;
  name: string;
  scheduleType: string;
  billingDayOfMonth: number;
  startMonth: number | null;
  discountCents: number;
  discountType: string;
  selectableOnEnrollment: boolean;
  appliesTo: readonly string[] | string[];
  description?: string | null;
}

interface TuitionPaymentOptionsSectionProps {
  /** The school year these payment options belong to. */
  schoolYearId: string;
  paymentOptions?: TuitionPaymentOption[];
  tuitionOptions?: TuitionOptionItem[];
  onRefetch?: () => void;
}

// Tuition Payment Options Section Component (Simplified - enum-based schedules)
export function TuitionPaymentOptionsSection({ schoolYearId, paymentOptions = [], tuitionOptions = [], onRefetch }: TuitionPaymentOptionsSectionProps) {
  const [localOptions, setLocalOptions] = useState<PaymentOptionEditState[]>(paymentOptions);
  const [showWizard, setShowWizard] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [wizardType, setWizardType] = useState('monthly');
  const [wizardBillingDay, setWizardBillingDay] = useState(1);
  const [wizardStartMonth, setWizardStartMonth] = useState(9);
  const [wizardName, setWizardName] = useState('');
  const [wizardDiscount, setWizardDiscount] = useState('0');
  const [wizardDiscountType, setWizardDiscountType] = useState('per_student');
  const [wizardSelectableOnEnrollment, setWizardSelectableOnEnrollment] = useState(true);
  const [wizardKey, setWizardKey] = useState('');

  const [updatePaymentOptions, { loading }] = useUpdateTuitionPaymentOptions();
  const [saveErrors, setSaveErrors] = useState<string[]>([]);

  // Generate default name based on schedule type and billing day
  const getDefaultName = (type: string, billingDay: number) => {
    const typeLabel = type === 'annual' ? 'Annual' : type === 'biannual' ? 'Semi-Annual' : 'Monthly';
    const dayLabel = billingDay === 1 ? '1st' : `${billingDay}th`;
    return `${typeLabel} (${dayLabel} of month)`;
  };

  const handleSaveOption = () => {
    const name = wizardName || getDefaultName(wizardType, wizardBillingDay);

    if (editingIndex !== null) {
      const updatedOption = {
        key: wizardKey,
        name,
        scheduleType: wizardType,
        billingDayOfMonth: wizardBillingDay,
        startMonth: wizardStartMonth,
        discountCents: parseToCents(wizardDiscount),
        discountType: wizardDiscountType,
        selectableOnEnrollment: wizardSelectableOnEnrollment,
        appliesTo: localOptions[editingIndex]?.appliesTo || tuitionOptions.map((t) => t.key),
      };

      const newOptions = [...localOptions];
      newOptions[editingIndex] = updatedOption;
      setLocalOptions(newOptions);
      saveOptions(newOptions);
    } else {
      let key = name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') || `option-${Date.now()}`;
      // Ensure key uniqueness
      const existingKeys = new Set(localOptions.map((o) => o.key));
      if (existingKeys.has(key)) {
        key = `${key}-${Date.now()}`;
      }

      const newOption = {
        key,
        name,
        scheduleType: wizardType,
        billingDayOfMonth: wizardBillingDay,
        startMonth: wizardStartMonth,
        discountCents: parseToCents(wizardDiscount),
        discountType: wizardDiscountType,
        selectableOnEnrollment: wizardSelectableOnEnrollment,
        appliesTo: tuitionOptions.map((t) => t.key),
      };

      const newOptions = [...localOptions, newOption];
      setLocalOptions(newOptions);
      saveOptions(newOptions);
    }

    setShowWizard(false);
    resetWizard();
  };

  const handleEditOption = (idx: number) => {
    const option = localOptions[idx];
    setEditingIndex(idx);
    setWizardKey(option.key);
    setWizardType(option.scheduleType || 'monthly');
    setWizardBillingDay(option.billingDayOfMonth || 1);
    setWizardStartMonth(option.startMonth || 9);
    setWizardName(option.name || '');
    setWizardDiscount(formatCents(option.discountCents || 0));
    setWizardDiscountType(option.discountType || 'per_student');
    setWizardSelectableOnEnrollment(option.selectableOnEnrollment !== false);
    setShowWizard(true);
  };

  const handleAddNew = () => {
    setEditingIndex(null);
    resetWizard();
    setShowWizard(true);
  };

  const resetWizard = () => {
    setEditingIndex(null);
    setWizardKey('');
    setWizardType('monthly');
    setWizardBillingDay(1);
    setWizardStartMonth(9);
    setWizardName('');
    setWizardDiscount('0');
    setWizardDiscountType('per_student');
    setWizardSelectableOnEnrollment(true);
  };

  const handleDeleteOption = (idx: number) => {
    const newOptions = localOptions.filter((_, i) => i !== idx);
    setLocalOptions(newOptions);
    saveOptions(newOptions);
  };

  const saveOptions = async (options: PaymentOptionEditState[]) => {
    const paymentOptionInputs = options.map(opt => ({
      key: opt.key,
      name: opt.name,
      scheduleType: opt.scheduleType,
      billingDayOfMonth: opt.billingDayOfMonth,
      startMonth: opt.startMonth,
      discountCents: opt.discountCents || 0,
      discountType: opt.discountType || 'per_student',
      selectableOnEnrollment: opt.selectableOnEnrollment !== false,
      appliesTo: opt.appliesTo || [],
    }));

    const errors = await saveErrorsFrom(async () => {
      const { data } = await updatePaymentOptions({
        variables: { input: { schoolYearId, paymentOptions: paymentOptionInputs } },
      });
      return data?.updateTuitionPaymentOptions?.userErrors;
    });
    setSaveErrors(errors);
    // The server refused it, so don't keep showing a change that didn't happen.
    if (errors.length > 0) setLocalOptions(paymentOptions);
    onRefetch?.();
  };

  // Get payment count description based on schedule type
  const getPaymentDescription = (scheduleType: string) => {
    switch (scheduleType) {
      case 'annual': return 'Annual payment';
      case 'biannual': return 'Semi-annual payments';
      case 'monthly': return 'Monthly payments throughout school year';
      default: return '';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Tuition Payment Options
        </CardTitle>
        <CardDescription>
          Define payment schedules families can choose from. Invoices are automatically generated based on schedule type.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {localOptions.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No payment options configured yet.
          </div>
        ) : (
          <div className="space-y-4">
            {localOptions.map((option, idx) => (
              <div key={option.key} className="border rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <div className="font-medium flex items-center gap-2">
                      {option.name}
                      {option.selectableOnEnrollment === false && (
                        <Badge variant="outline" className="text-xs flex items-center gap-1">
                          <EyeOff className="h-3 w-3" />
                          Admin only
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {SCHEDULE_TYPE_LABELS[option.scheduleType] || option.scheduleType}
                      {option.startMonth && (
                        <span> • Starts {MONTH_OPTIONS.find(m => m.value === option.startMonth)?.label || option.startMonth}</span>
                      )}
                      {option.billingDayOfMonth && (
                        <span> • Due on {option.billingDayOfMonth === 1 ? '1st' : `${option.billingDayOfMonth}th`}</span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {getPaymentDescription(option.scheduleType)}
                    </div>
                    {option.discountCents > 0 && (
                      <Badge variant="secondary" className="mt-1">
                        ${formatCents(option.discountCents)} {option.discountType === 'per_student' ? 'per student' : 'flat'} discount
                      </Badge>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditOption(idx)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteOption(idx)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <SaveErrors errors={saveErrors} />

        <Button onClick={handleAddNew}>
          <Plus className="h-4 w-4 mr-2" />
          Add Payment Option
        </Button>

        {/* Add/Edit Payment Option Wizard Dialog */}
        <Dialog open={showWizard} onOpenChange={(open) => { setShowWizard(open); if (!open) resetWizard(); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingIndex !== null ? 'Edit Payment Option' : 'Add Payment Option'}</DialogTitle>
              <DialogDescription>
                {editingIndex !== null
                  ? 'Update this payment schedule option.'
                  : 'Create a new payment schedule option for families.'
                }
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Schedule Type</Label>
                <Select value={wizardType} onValueChange={setWizardType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="biannual">Semi-Annual</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {getPaymentDescription(wizardType)}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Month</Label>
                  <Select value={String(wizardStartMonth)} onValueChange={(v) => setWizardStartMonth(parseInt(v))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTH_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={String(opt.value)}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    When payments begin
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Billing Day of Month</Label>
                  <Select value={String(wizardBillingDay)} onValueChange={(v) => setWizardBillingDay(parseInt(v))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BILLING_DAY_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={String(opt.value)}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Day payments are due
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Name (optional)</Label>
                <Input
                  placeholder={getDefaultName(wizardType, wizardBillingDay)}
                  value={wizardName}
                  onChange={(e) => setWizardName(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Leave blank to use the default name.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Discount Amount ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={wizardDiscount}
                    onChange={(e) => setWizardDiscount(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Discount Type</Label>
                  <Select value={wizardDiscountType} onValueChange={setWizardDiscountType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="per_student">Per Student</SelectItem>
                      <SelectItem value="flat">Flat</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="selectableOnEnrollment"
                  checked={wizardSelectableOnEnrollment}
                  onCheckedChange={(checked) => setWizardSelectableOnEnrollment(checked as boolean)}
                />
                <Label htmlFor="selectableOnEnrollment" className="text-sm font-normal cursor-pointer">
                  Families can select this option during enrollment
                </Label>
              </div>
              {!wizardSelectableOnEnrollment && (
                <p className="text-xs text-muted-foreground pl-6">
                  This option will only be available to families you specifically enable it for (e.g., staff discount).
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowWizard(false)}>Cancel</Button>
              <Button onClick={handleSaveOption} disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                {editingIndex !== null ? 'Save Changes' : 'Create Payment Option'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

interface EnrollmentFeeSectionProps {
  /** The school year this enrollment fee belongs to. */
  schoolYearId: string;
  enrollmentFee?: EnrollmentFeeSetting;
  onRefetch?: () => void;
}

// Enrollment Fee Section Component
export function EnrollmentFeeSection({ schoolYearId, enrollmentFee, onRefetch }: EnrollmentFeeSectionProps) {
  const [localFee, setLocalFee] = useState<Record<string, string | number | null | undefined>>(enrollmentFee ? { ...enrollmentFee } : {});
  const [amountDisplay, setAmountDisplay] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [lastValidAmountCents, setLastValidAmountCents] = useState(0);

  const [updateEnrollmentFee, { loading }] = useUpdateEnrollmentFee();
  const [saveErrors, setSaveErrors] = useState<string[]>([]);

  // Initialize amount display value when enrollmentFee changes
  useEffect(() => {
    const cents = enrollmentFee?.amountCents || 0;
    setAmountDisplay(formatCents(cents));
    setLastValidAmountCents(cents);
    setLocalFee(enrollmentFee ? { ...enrollmentFee } : {});
  }, [enrollmentFee]);

  const handleChange = (field: string, value: string) => {
    setLocalFee(prev => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const handleAmountChange = (value: string) => {
    setAmountDisplay(value);
  };

  const handleAmountBlur = () => {
    const numValue = parseFloat(amountDisplay);
    if (isNaN(numValue) || numValue < 0) {
      setAmountDisplay(formatCents(lastValidAmountCents));
      return;
    }

    const cents = parseToCents(amountDisplay);
    setLastValidAmountCents(cents);
    handleChange('amountCents', String(cents));
  };

  const handleSave = async () => {
    const numValue = parseFloat(amountDisplay);
    if (isNaN(numValue) || numValue < 0) {
      setAmountDisplay(formatCents(lastValidAmountCents));
      return;
    }

    const finalAmountCents = parseToCents(amountDisplay);
    if (!Number.isInteger(finalAmountCents) || finalAmountCents < 0) {
      setAmountDisplay(formatCents(lastValidAmountCents));
      return;
    }

    const errors = await saveErrorsFrom(async () => {
      const { data } = await updateEnrollmentFee({
        variables: {
          input: {
            schoolYearId,
            name: localFee.name as string | undefined,
            description: localFee.description as string | null | undefined,
            amountCents: finalAmountCents,
          },
        },
      });
      return data?.updateEnrollmentFee?.userErrors;
    });
    setSaveErrors(errors);
    if (errors.length === 0) {
      setIsDirty(false);
      onRefetch?.();
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DollarSign className="h-5 w-5" />
          Enrollment Fee
        </CardTitle>
        <CardDescription>
          Per-student fee charged upon enrollment to complete the enrollment process.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>Fee Name</Label>
            <Input
              value={localFee.name || 'Enrollment Fee'}
              onChange={(e) => handleChange('name', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Amount ($)</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
              <Input
                type="text"
                value={amountDisplay}
                onChange={(e) => handleAmountChange(e.target.value)}
                onBlur={handleAmountBlur}
                className="pl-7"
                placeholder="0.00"
              />
            </div>
          </div>
        </div>
        <div className="space-y-2">
          <Label>Description</Label>
          <Input
            value={localFee.description || ''}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="e.g., Non-refundable enrollment fee per student"
          />
        </div>

        <SaveErrors errors={saveErrors} />

        {isDirty && (
          <Button onClick={handleSave} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export default TuitionOptionsEditor;
